import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { isDesktopBreakpoint, setInnerHTMLAndExecuteScripts, fetchConfig } from '@theme/utilities';
import { normalizeSectionId, PrefetchCache } from '@theme/section-renderer';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

export class QuickActionComponent extends Component {
  /** @type {PrefetchCache<Element>} */
  #cache = new PrefetchCache({
    fetcher: (url, signal) => this.#fetchSection(url, signal),
    maxSize: QuickActionComponent.MAX_CACHE_SIZE,
    shouldCache: () => !Shopify.designMode,
  });
  /** @type {number | null} */
  #hoverTimeout = null;
  /** @type {string | null} */
  #quickAddSectionId = null;
  /** @type {boolean} */
  #isClickFetching = false;

  // Constants for selectors and timing
  static HOVER_DELAY = 150;
  static MAX_CACHE_SIZE = 20;
  static SELECTORS = {
    QUICK_ACTION_SECTION: '.shopify-section-overlay-quick-add',
    QUICK_ACTION_TEMPLATE: '.quick-action-template',
    QUICK_ACTION_DIALOG: '#quick-add-dialog'
  };

  get selectors() {
    return this.constructor.SELECTORS;
  }

  get productPageUrl() {
    const productLink = this.getAttribute('data-product-link');
    if (!productLink) return '';
    return productLink;
  }

  get productPageQuickAddURL() {
    return this.buildFetchUrlFromProductHref(this.productPageUrl);
  }

  /**
   * Products with selling plans must be bought on the product page — subscription
   * widgets are app blocks that only initialise there, so the modal would let the
   * customer add a one-time purchase instead of the plan they picked.
   * @returns {boolean}
   */
  get usesSellingPlans() {
    return this.dataset.usesSellingPlans === 'true';
  }

  /**
   * Build the fetch URL for a given product page href.
   * Subclasses can override to use a different URL format (e.g. `?view=quick-edit`).
   * @param {string} productPageHref
   * @returns {string}
   */
  buildFetchUrlFromProductHref(productPageHref) {
    if (!productPageHref) return '';
    const url = new URL(productPageHref, window.location.origin);
    if (this.quickAddSectionId) {
      url.searchParams.set('section_id', this.quickAddSectionId);
    }
    return url.toString();
  }

  /**
   * True if this instance already has cached or in-flight prefetch for this product.
   * @param {string} productPageHref
   * @returns {boolean}
   */
  hasWarmCacheForProductUrl(productPageHref) {
    const key = this.buildFetchUrlFromProductHref(productPageHref);
    if (!key) return false;
    return this.#cache.has(key);
  }

  /**
   * Prefer an instance that already has hover prefetch for this URL (same keys as `handleClick`).
   * @param {string} tagName - e.g. `quick-add-component`
   * @param {string} productPageUrl
   * @returns {QuickActionComponent | null}
   */
  static findInstanceForProductUrl(tagName, productPageUrl) {
    const candidates = document.querySelectorAll(tagName);
    /** @type {QuickActionComponent | null} */
    let fallback = null;
    for (const el of candidates) {
      if (!(el instanceof QuickActionComponent)) continue;
      if (el.usesSellingPlans) continue;
      if (!fallback) fallback = el;
      if (el.hasWarmCacheForProductUrl(productPageUrl)) return el;
    }
    return fallback;
  }

  /**
   * Prefetch quick-add section HTML for a product URL (same cache + pending map as the quick-add button hover).
   * Caller should debounce (e.g. {@link QuickActionComponent.HOVER_DELAY}) for pointer hover.
   * @param {string} productPageHref - Product page href
   */
  enqueueHoverPrefetchForUrl(productPageHref) {
    if (Shopify.designMode) return;
    const currentUrl = this.buildFetchUrlFromProductHref(productPageHref);
    if (!currentUrl) return;
    if (!isDesktopBreakpoint()) return;
    if (this.#isClickFetching) return;

    this.#cache.prefetch(currentUrl);
  }

  get isProductFormComponent() {
    return this.querySelector('product-form-component');
  }

  connectedCallback() {
    super.connectedCallback();

    // Setup hover functionality for quick add button
    this.#setupHoverEvents();

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', (e) => {
        this.#cache.clear();
      });
    }

  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Cleanup hover events
    this.removeEventListener('pointerenter', this.#handleHover);
    this.removeEventListener('focusin', this.#handleHover);
    this.removeEventListener('pointerleave', this.#handleHoverOut);
    this.removeEventListener('focusout', this.#handleHoverOut);

    // Cleanup design mode listener
    if (Shopify.designMode && this.handleBlockSelect) {
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }

    this.#clearHoverTimeout();
    // Abort pending request + drop cache
    this.#cache.abort();
    this.#cache.clear();
  }

  /**
   * Setup hover events for quick add button.
   */
  #setupHoverEvents() {
    // Add hover delay to avoid fetch on accidental hover
    this.addEventListener('pointerenter', this.#handleHover);
    this.addEventListener('focusin', this.#handleHover);
    this.addEventListener('pointerleave', this.#handleHoverOut);
    this.addEventListener('focusout', this.#handleHoverOut);
  }

  /**
   * Handle hover in event with delay.
   */
  #handleHover = () => {
    if (Shopify.designMode) return;
    if (this.usesSellingPlans) return;
    if (!this.productPageUrl) return;
    if (!isDesktopBreakpoint()) return;
    if (this.isProductFormComponent) {
      return;
    }
    this.#clearHoverTimeout();

    this.#hoverTimeout = setTimeout(() => {
      this.enqueueHoverPrefetchForUrl(this.productPageUrl);
    }, QuickActionComponent.HOVER_DELAY);
  };

  /**
   * Handle hover out event.
   */
  #handleHoverOut = () => {
    this.#clearHoverTimeout();
  };

  /**
   * Clear hover timeout.
   */
  #clearHoverTimeout() {
    if (this.#hoverTimeout) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }
  }


  /**
   * Returns the quick action section element (modal content container).
   * Falls back to quick-add section if primary selector finds nothing (e.g. quick-view when only quick-add overlay exists).
   * @param {Document | Element} [root=document] - Root to query in (document or parsed HTML document).
   * @returns {Element | null}
   */
  getQuickActionSectionElement(root = document) {
    let sectionEl = root.querySelector(this.selectors.QUICK_ACTION_SECTION);
    if (sectionEl === null) {
      sectionEl = root.querySelector('.shopify-section-overlay-quick-add');
    }
    return sectionEl;
  }

  /**
   * Handles quick add button click.
   * @param {Event} event - Click event
   */
  handleClick = async (event = null) => {
    if (event) {
      event.preventDefault();
    }
    if (this.usesSellingPlans) {
      if (this.productPageUrl) location.href = this.productPageUrl;
      return;
    }
    const sectionEl = this.getQuickActionSectionElement();
    if (sectionEl === null) {
      location.href = this.productPageUrl;
      return;
    }
    const currentUrl = this.productPageQuickAddURL;
    const quickAddButton = event?.target;
    if (quickAddButton) {
      quickAddButton.classList.add('btn--loading');
    }

    // Cancel any hover prefetch and resolve via the shared cache
    // (cache hit → in-flight prefetch → fresh fetch).
    this.#clearHoverTimeout();
    this.#isClickFetching = true;
    let productGrid;
    try {
      productGrid = await this.#cache.get(currentUrl);
    } finally {
      this.#isClickFetching = false;
    }

    if (productGrid) {
      const freshContent = productGrid.cloneNode(true);
      await this.updateQuickAddModal(freshContent);
    }

    if (quickAddButton) {
      quickAddButton.classList.remove('btn--loading');
    }

    this.openQuickActionModal();
  };

  /**
   * Opens the same quick-add / quick-view flow as {@link handleClick} for an arbitrary product URL
   * (e.g. sibling links in the modal). Temporarily sets `data-product-link`, then restores it.
   * @param {string} productPageUrl - Product page href (relative or absolute)
   */
  loadProductFromUrl = async (productPageUrl) => {
    if (!productPageUrl) return;
    const previous = this.getAttribute('data-product-link');
    this.setAttribute('data-product-link', productPageUrl);
    try {
      await this.handleClick(null);
    } finally {
      if (previous != null && previous !== '') {
        this.setAttribute('data-product-link', previous);
      } else {
        this.removeAttribute('data-product-link');
      }
    }
  };

  /**
   * Fetcher for the prefetch cache: fetch the product page and extract a clone of
   * the quick-action section element. Aborts are surfaced as `null`.
   * @param {string} url - URL to fetch
   * @param {AbortSignal} signal - Abort signal from the cache
   * @returns {Promise<Element | null>}
   */
  async #fetchSection(url, signal) {
    const html = await this.fetchProductPage(url, signal);
    if (!html) return null;

    const gridElement = this.getQuickActionSectionElement(html);
    if (!gridElement) return null;
    return /** @type {Element} */ (gridElement.cloneNode(true));
  }

  /**
   * Opens quick add modal dialog.
   */
  openQuickActionModal() {
    let dialogComponent = document.querySelector(this.selectors.QUICK_ACTION_DIALOG);
    if (!dialogComponent) {
      dialogComponent = document.querySelector('#quick-add-dialog');
    }
    if(!dialogComponent) return;
    if (!(dialogComponent instanceof QuickAddDialog) && !(dialogComponent instanceof QuickViewDialog)) return;

    dialogComponent.openDialog();
  };

  /**
   * Fetches product page content. Abort is handled by the prefetch cache via `signal`.
   * @param {string} productPageUrl - URL of product page to fetch
   * @param {AbortSignal} [signal] - Optional abort signal
   * @returns {Promise<Document | null>} Parsed HTML document or null
   */
  async fetchProductPage(productPageUrl, signal) {
    if (!productPageUrl) {
      return null;
    }

    try {
      const response = await fetch(productPageUrl, { signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    }
  }

  /**
   * Update quick add modal content.
   * @param {Element} productGrid - Product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = this.getQuickActionSectionElement();
    if (!productGrid || !modalContent) return;

    const sourceSection = /** @type {Element} */ (productGrid.cloneNode(true));

    sourceSection.querySelectorAll(this.selectors.QUICK_ACTION_TEMPLATE).forEach((templateElement) => {
      const fragment = templateElement.content.cloneNode(true);
      templateElement.replaceWith(fragment);
    });

    setInnerHTMLAndExecuteScripts(modalContent, sourceSection.innerHTML);

    if (window.Shopify && Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }
  }

  get quickAddSectionId() {
    if (!this.#quickAddSectionId || Shopify.designMode) {
      const quickAddSection = this.getQuickActionSectionElement();
      if (!quickAddSection || !quickAddSection.id) return;
      this.#quickAddSectionId = normalizeSectionId(quickAddSection.id);
    }

    return this.#quickAddSectionId;
  }
}

export class QuickAddComponent extends QuickActionComponent {
}

export class QuickViewComponent extends QuickActionComponent {

  static SELECTORS = {
    QUICK_ACTION_SECTION: '.shopify-section-overlay-quick-view',
    QUICK_ACTION_TEMPLATE: '.quick-action-template',
    QUICK_ACTION_DIALOG: '#quick-view-dialog'
  };

  get isProductFormComponent() {
    return false;
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
if (!customElements.get('quick-view-component')) {
  customElements.define('quick-view-component', QuickViewComponent);
}

class QuickActionDialog extends DialogComponent {
  /** @type {Map<Element, ReturnType<typeof setTimeout>>} */
  #siblingPrefetchTimeouts = new Map();
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section');

    this.querySelectorAll('a.product-siblings__link').forEach((link) => {
      link.addEventListener('click', this.handleProductSiblingClick);
      link.addEventListener('pointerenter', this.handleProductSiblingPointerEnter);
      link.addEventListener('pointerleave', this.handleProductSiblingPointerLeave);
      link.addEventListener('focusin', this.handleProductSiblingPointerEnter);
      link.addEventListener('focusout', this.handleProductSiblingPointerLeave);
    });
  }

  /**
   * Same hover prefetch as the quick-add button: delayed fetch into `QuickActionComponent` cache.
   */
  handleProductSiblingPointerEnter = (event) => {
    const anchor = event.currentTarget;
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const href = anchor.getAttribute('href');
    if (!href) return;

    const existing = this.#siblingPrefetchTimeouts.get(anchor);
    if (existing !== undefined) {
      clearTimeout(existing);
    }

    const quickActionComponentTag =
      this instanceof QuickViewDialog ? 'quick-view-component' : 'quick-add-component';
    const timeoutId = setTimeout(() => {
      this.#siblingPrefetchTimeouts.delete(anchor);
      const host = QuickActionComponent.findInstanceForProductUrl(quickActionComponentTag, href);
      host?.enqueueHoverPrefetchForUrl(href);
    }, QuickActionComponent.HOVER_DELAY);
    this.#siblingPrefetchTimeouts.set(anchor, timeoutId);
  };

  handleProductSiblingPointerLeave = (event) => {
    const anchor = event.currentTarget;
    const existing = this.#siblingPrefetchTimeouts.get(anchor);
    if (existing !== undefined) {
      clearTimeout(existing);
      this.#siblingPrefetchTimeouts.delete(anchor);
    }
  };

  /**
   * Same pipeline as `quick-add-component` / `handleClick`: load sibling product into this dialog.
   * Falls back to full navigation if no `QuickActionComponent` instance is found on the page.
   */
  handleProductSiblingClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.#section?.classList.add('product-siblings-overlay-opening');
    this.#section?.classList.add('product-siblings-overlay-skip');

    const anchor = event.currentTarget;
    const productPageUrl = anchor.getAttribute('href');
    if (!productPageUrl) return;

    let bouncingDots = anchor.querySelector('.bouncing-dots');
    if (!bouncingDots) {
      bouncingDots = document.createElement('span');
      bouncingDots.className = 'bouncing-dots';
      for (let i = 0; i < 3; i++) {
        bouncingDots.appendChild(document.createElement('span'));
      }
      anchor.appendChild(bouncingDots);
    }

    anchor.classList.add('btn--loading');

    const quickActionComponentTag =
      this instanceof QuickViewDialog ? 'quick-view-component' : 'quick-add-component';
    const quickActionComponent = QuickActionComponent.findInstanceForProductUrl(
      quickActionComponentTag,
      productPageUrl
    );

    try {
      if (quickActionComponent && typeof quickActionComponent.loadProductFromUrl === 'function') {
        await quickActionComponent.loadProductFromUrl(productPageUrl);
      } else {
        window.location.assign(productPageUrl);
      }
    } finally {
      anchor.classList.remove('btn--loading');
    }
  };

  async openDialog(e) {
    await super.openDialog(e);
    this.#section?.classList.remove('product-siblings-overlay-opening');
  }

  closeDialog(e) {
    const isSiblingClick = e?.target?.closest?.('.product-siblings__link');
    if (!isSiblingClick) {
      this.#section?.classList.remove('product-siblings-overlay-skip');
    }
    return super.closeDialog(e);
  }

  disconnectedCallback() {
    this.querySelectorAll('a.product-siblings__link').forEach((link) => {
      const id = this.#siblingPrefetchTimeouts.get(link);
      if (id !== undefined) {
        clearTimeout(id);
      }
      this.#siblingPrefetchTimeouts.delete(link);
      link.removeEventListener('click', this.handleProductSiblingClick);
      link.removeEventListener('pointerenter', this.handleProductSiblingPointerEnter);
      link.removeEventListener('pointerleave', this.handleProductSiblingPointerLeave);
      link.removeEventListener('focusin', this.handleProductSiblingPointerEnter);
      link.removeEventListener('focusout', this.handleProductSiblingPointerLeave);
    });

    super.disconnectedCallback();

    const section = this.closest('.shopify-section');
    if (section) {
      if (this.handleSectionSelect) {
        section.removeEventListener('shopify:section:select', this.handleSectionSelect);
      }
      if (this.handleSectionDeselect) {
        section.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
      }
    }
    if (this.handleBlockSelect) {
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }
  }

  /**
   * Handles design mode events for dialog.
   */
  handleDesignMode() {
    const section = this.closest('.shopify-section');
    this.handleSectionSelect = (event) => {
      this.openDialog();
    };
    section.addEventListener('shopify:section:select', this.handleSectionSelect);
    section.addEventListener('shopify:section:load', this.handleSectionSelect);

    this.handleSectionDeselect = (event) => {
      this.closeDialog();
    };
    section.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
    section.addEventListener('shopify:section:unload', this.handleSectionDeselect);
    this.handleBlockSelect = (event) => {
      if (this.contains(event.target)) {
        this.openDialog();
      }
    };
    this.addEventListener('shopify:block:select', this.handleBlockSelect);
  }
}

class QuickViewDialog extends QuickActionDialog {
}

class QuickAddDialog extends QuickActionDialog {
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
if (!customElements.get('quick-view-dialog')) {
  customElements.define('quick-view-dialog', QuickViewDialog);
}

export class QuickEditComponent extends QuickActionComponent {
  static SELECTORS = {
    QUICK_ACTION_SECTION: '.shopify-section-overlay-quick-edit',
    QUICK_ACTION_TEMPLATE: '.quick-action-template',
    QUICK_ACTION_DIALOG: '#quick-edit-dialog'
  };

  /** @type {number | null} */
  #errorTimeout = null;

  get isProductFormComponent() {
    return false;
  }

  /**
   * Build fetch URL using the quick-edit section rendering response.
   * Used by both hover prefetch and click so they share the same cache key.
   * @param {string} productPageHref
   * @returns {string}
   */
  buildFetchUrlFromProductHref(productPageHref) {
    if (!productPageHref) return '';
    const url = new URL(productPageHref, window.location.origin);
    url.searchParams.set('section_id', 'quick-edit');
    if (this.dataset.variantId) {
      url.searchParams.set('variant', this.dataset.variantId);
    }
    return url.toString();
  }

  /**
   * Override to create the container lazily — quick-edit is NOT pre-rendered in overlay-group.
   * When root === document (live DOM lookup), create the container if missing.
   * When root is a parsed fetch Document, query normally (section HTML has the class).
   * @param {Document | Element} [root=document]
   * @returns {Element | null}
   */
  getQuickActionSectionElement(root = document) {
    let sectionEl = root.querySelector(this.selectors.QUICK_ACTION_SECTION);
    if (sectionEl === null && root === document) {
      sectionEl = document.createElement('div');
      sectionEl.className = 'shopify-section shopify-section-overlay-quick-edit';
      document.body.appendChild(sectionEl);
    }
    return sectionEl;
  }

  openQuickActionModal() {
    const dialogComponent = document.querySelector(this.selectors.QUICK_ACTION_DIALOG);
    if (!dialogComponent || !(dialogComponent instanceof QuickEditDialog)) return;
    // Track which line-key opened the dialog so design mode can re-open the correct item on section reload
    if (Shopify.designMode && this.dataset.lineKey) {
      dialogComponent.setLastLineKey(this.dataset.lineKey);
    }
    dialogComponent.openDialog();
  }

  async updateQuickAddModal(productGrid) {
    await super.updateQuickAddModal(productGrid);

    const dialog = document.querySelector(this.selectors.QUICK_ACTION_DIALOG);
    if (!dialog) return;

    if (this.dataset.showMedia === 'false') {
      dialog.querySelector('.product-information__media')?.remove();
    }
    if (this.dataset.showBadges === 'false') {
      dialog.querySelector('.product-badges')?.remove();
    }
    if (this.dataset.showQuantity === 'false') {
      dialog.querySelector('.quantity-selector')?.classList.add('!hidden');
    }

    // Sync quantity input to current cart line item quantity
    const qtyInput = dialog.querySelector('input[name="quantity"]');
    if (qtyInput && this.dataset.quantity) {
      qtyInput.value = this.dataset.quantity;
    }
    const quantitySelector = dialog.querySelector('quantity-selector-component');
    if (quantitySelector && typeof quantitySelector.syncButtonStates === 'function') {
      quantitySelector.syncButtonStates();
    }

    this.#bindUpdateButtonState(dialog);

    const updateBtn = dialog.querySelector('[ref="quickEditUpdate"]');
    if (updateBtn) {
      updateBtn.addEventListener('click', this.#handleUpdate);
    }
  }

  #bindUpdateButtonState(dialog) {
    const updateBtn = dialog.querySelector('[ref="quickEditUpdate"]');
    if (!updateBtn) return;

    const initialVariantId = this.dataset.variantId;
    const initialQty = this.dataset.quantity;

    // Track current variant state in local variables.
    // We cannot read input[name="id"] on variant:selected because that event fires before the
    // async fetch resolves — the input still holds the previous value at that point.
    // variant:update fires after the fetch and carries the resolved ID + availability.
    let currentVariantId = initialVariantId;
    let currentVariantAvailable = true; // initial variant is already in cart, treat as available

    const checkChanged = () => {
      if (!currentVariantAvailable) {
        updateBtn.disabled = true;
        return;
      }
      const qtyInput = dialog.querySelector('input[name="quantity"]');
      const currentQty = qtyInput?.value ?? initialQty;
      const changed = currentVariantId !== initialVariantId || currentQty !== initialQty;
      updateBtn.disabled = !changed;
    };

    // variant:update fires after the async section fetch with the resolved variant ID + stock
    dialog.addEventListener('variant:update', (event) => {
      const newId = String(event.detail?.resource?.id ?? '');
      if (newId) currentVariantId = newId;
      currentVariantAvailable = event.detail?.resource?.available !== false;
      checkChanged();
    });

    dialog.addEventListener('quantity-selector:update', checkChanged);
    dialog.addEventListener('change', checkChanged);
  }

  /**
   * Show an error message inside the quick-edit dialog, then auto-hide after 5 s.
   * @param {Element} dialog
   * @param {string} message
   */
  #showUpdateError(dialog, message) {
    const errorEl = dialog.querySelector('[ref="quickEditError"]');
    const errorTextEl = dialog.querySelector('[ref="quickEditTextError"]');
    const liveRegion = dialog.querySelector('[ref="quickEditLiveRegion"]');

    if (errorEl) errorEl.classList.remove('hidden');
    if (errorTextEl) errorTextEl.textContent = message;
    if (liveRegion) liveRegion.textContent = message;

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => {
      if (errorEl) errorEl.classList.add('hidden');
      if (liveRegion) liveRegion.textContent = '';
      this.#errorTimeout = null;
    }, 5000);
  }

  /**
   * First error string from a cart API JSON response, or null if OK.
   * @param {object} data
   * @returns {string | null}
   */
  #getCartApiErrorMessage(data) {
    if (data?.errors) return String(data.errors);
    if (data?.status) return data.description || data.message;
    return null;
  }

  /**
   * POST cart/update for several lines at once, so a variant swap cannot leave the old line
   * behind when the second half fails.
   * Returns the cart response data on success, or null if Shopify returned an error (message already shown).
   * @param {Element} dialog
   * @param {Object<string, number>} updates - Line item keys or variant ids mapped to quantities
   * @param {string} [sections] - Comma-separated section IDs to re-render in the response
   * @returns {Promise<Object|null>}
   */
  async #updateCartLines(dialog, updates, sections = '') {
    const body = { updates };
    if (sections) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    const updateResponse = await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
      body: JSON.stringify(body)
    }));
    const updateData = await updateResponse.json();
    const msg = this.#getCartApiErrorMessage(updateData);
    if (msg) {
      this.#showUpdateError(dialog, msg);
      return null;
    }
    return updateData;
  }

  /**
   * Wait until cart/cart-drawer DOM finishes re-rendering before continuing.
   * Waits only for this quick-edit update source; falls back by timeout.
   * @param {string} sourceId
   * @param {number} timeoutMs
   * @returns {Promise<void>}
   */
  #waitForCartUpdated(sourceId, timeoutMs = 1500) {
    return new Promise((resolve) => {
      let timeoutId = null;
      const onUpdated = (event) => {
        if (event.detail?.sourceId !== sourceId) return;
        if (timeoutId) clearTimeout(timeoutId);
        document.removeEventListener(ThemeEvents.cartUpdated, onUpdated);
        resolve();
      };

      document.addEventListener(ThemeEvents.cartUpdated, onUpdated);
      timeoutId = window.setTimeout(() => {
        document.removeEventListener(ThemeEvents.cartUpdated, onUpdated);
        resolve();
      }, timeoutMs);
    });
  }

  #handleUpdate = async () => {
    const dialog = document.querySelector(this.selectors.QUICK_ACTION_DIALOG);
    if (!dialog) return;

    const variantInput = dialog.querySelector('input[name="id"]');
    const qtyInput = dialog.querySelector('input[name="quantity"]');

    const newVariantId = variantInput?.value;
    const newQty = parseInt(qtyInput?.value || '1', 10);
    const cartLineItemKey = this.dataset.lineKey;
    const initialVariantId = this.dataset.variantId;

    const updateBtn = dialog.querySelector('[ref="quickEditUpdate"]');
    if (updateBtn) updateBtn.classList.add('btn--loading');

    try {
      const variantChanged = newVariantId && newVariantId !== initialVariantId;

      // Collect cart section IDs so the last cart API call returns section HTML,
      // allowing FreeShippingBar and other components to update via events (same as CartItemsComponent.updateQuantity).
      const sectionsToUpdate = new Set();
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el.dataset.sectionId) sectionsToUpdate.add(el.dataset.sectionId.replace('shopify-section-', ''));
      });
      const announcementBar = document.querySelector('announcement-bar');
      if (announcementBar?.querySelector('free-shipping-bar')) {
        sectionsToUpdate.add(announcementBar.dataset.sectionId);
      }
      const sectionsParam = Array.from(sectionsToUpdate).join(',');

      const updates = variantChanged
        ? { [cartLineItemKey]: 0, [newVariantId]: newQty }
        : { [cartLineItemKey]: newQty };
      const cartData = await this.#updateCartLines(dialog, updates, sectionsParam);
      if (!cartData) return;

      const sourceId = this.id || 'quick-edit';
      document.dispatchEvent(new CartUpdateEvent({}, sourceId, { sections: cartData.sections || {} }));
      await this.#waitForCartUpdated(sourceId);

      const dialogComponent = document.querySelector(this.selectors.QUICK_ACTION_DIALOG);
      if (dialogComponent instanceof QuickEditDialog) {
        dialogComponent.closeDialog();
      }
    } catch (error) {
      console.error('Quick edit update failed:', error);
    } finally {
      if (updateBtn) updateBtn.classList.remove('btn--loading');
    }
  };
}

class QuickEditDialog extends QuickActionDialog {
  /** @type {string | null} */
  #lastLineKey = null;

  /**
   * Remember which cart line last opened this dialog.
   * Called by QuickEditComponent.openQuickActionModal() in design mode.
   * @param {string} lineKey
   */
  setLastLineKey(lineKey) {
    this.#lastLineKey = lineKey;
  }

  handleDesignMode() {
    super.handleDesignMode();

    const signal = this.abortController.signal;

    // When a section containing quick-edit-components reloads (e.g. cart section after
    // changing enable_quick_edit / show_media / show_badges / show_quantity),
    // re-open the dialog for the same cart line that was last edited.
    document.addEventListener('shopify:section:load', async (event) => {
      const loadedSection = /** @type {HTMLElement} */ (event.target);
      if (!loadedSection.querySelector('quick-edit-component')) return;

      // Prefer the component matching the last-opened line key; fall back to the first one
      // only when the dialog has never been opened yet (initial editor preview).
      const selector = this.#lastLineKey
        ? `quick-edit-component[data-line-key="${CSS.escape(this.#lastLineKey)}"]`
        : 'quick-edit-component';
      const targetComponent = loadedSection.querySelector(selector);
      if (!(targetComponent instanceof QuickEditComponent)) return;

      // handleClick re-fetches in design mode (cache bypassed) and re-applies
      // show_media / show_badges / show_quantity from the component's new data attributes.
      await targetComponent.handleClick();
    }, { signal });
  }
}

if (!customElements.get('quick-edit-component')) {
  customElements.define('quick-edit-component', QuickEditComponent);
}
if (!customElements.get('quick-edit-dialog')) {
  customElements.define('quick-edit-dialog', QuickEditDialog);
}